/**
 * Deterministic case-study generator.
 *
 * Builds 1,000+ situational case examples per service from real axes
 * (document type x destination x Thai region x audience group) so no two
 * records are a word-swap of each other. No AI credits, stable output.
 */
import type { CaseStudy, CaseTopic } from "./types";
import {
  AUDIENCES,
  DESTINATIONS,
  HURDLES,
  OUTCOMES,
  REGION_SLUGS,
  REGION_TH,
  TURNAROUNDS,
  pick,
  provincesByRegion,
} from "./pools";

export type ServiceCaseConfig = {
  slug: string;
  th: string;
  /** hub route of the service, e.g. "/notary" */
  hubHref: string;
  /** one-line description used in hub copy */
  summaryTh: string;
  /** document types this service handles (slug + Thai label) */
  documents: Array<{ slug: string; th: string }>;
  /** what the paperwork is used for */
  purposesTh: string[];
  /** service steps we take care of, phrased as work we perform */
  stepsTh: string[];
  /** related services the same case often bundles */
  extrasTh: string[];
  authorityTh: string;
  citationTh: string;
};

const TARGET_PER_SERVICE = 1040;

function caseFor(
  cfg: ServiceCaseConfig,
  topicSlug: string,
  topicTh: string,
  i: number,
  fixed: Partial<Pick<CaseStudy, "locationTh" | "audienceTh" | "nationalityTh">> & { destinationTh?: string },
): CaseStudy {
  const doc = pick(cfg.documents, i * 3 + 1);
  const audience = pick(AUDIENCES, i * 5 + 2);
  const audienceTh = fixed.audienceTh ?? audience.th;
  const nationalityTh = fixed.nationalityTh ?? pick(audience.nationalities, i * 7 + 3);
  const clientKindTh = nationalityTh === "ชาวไทย" ? "ชาวไทย" : "ชาวต่างชาติ";
  const dest = pick(DESTINATIONS, i * 11 + 4);
  const destinationTh = fixed.destinationTh ?? pick(dest.countries, i * 13 + 5);
  const region = pick(REGION_SLUGS, i * 17 + 6);
  const locationTh = fixed.locationTh ?? pick(provincesByRegion(region), i * 19 + 7);
  const hurdle = pick(HURDLES, i * 23 + 8);
  const purposeTh = pick(cfg.purposesTh, i * 29 + 9);
  const extraCount = i % 3; // 0-2 extra services
  const extraServicesTh = Array.from({ length: extraCount }, (_, k) => pick(cfg.extrasTh, i * 31 + k + 10));
  const secondDoc = pick(cfg.documents, i * 37 + 11);
  const documentsTh = Array.from(new Set([doc.th, secondDoc.th]));
  const steps = [
    pick(cfg.stepsTh, i * 41 + 12),
    pick(cfg.stepsTh, i * 43 + 13),
    pick(cfg.stepsTh, i * 47 + 14),
  ];
  const stepsTh = Array.from(new Set(steps));

  return {
    id: `${cfg.slug}-${topicSlug}-${String(i + 1).padStart(3, "0")}`,
    serviceSlug: cfg.slug,
    serviceTh: cfg.th,
    extraServicesTh: Array.from(new Set(extraServicesTh)),
    clientKindTh,
    nationalityTh,
    locationTh,
    audienceTh,
    purposeTh: `${purposeTh} ที่${destinationTh}`,
    documentsTh,
    stepsTh,
    obstacleTh: hurdle.obstacle,
    resolutionTh: hurdle.resolution,
    turnaroundTh: pick(TURNAROUNDS, i * 53 + 15),
    outcomeTh: pick(OUTCOMES, i * 59 + 16),
    topicSlug,
    topicTh,
  };
}

/** Build the topic (sub-hub) axes for one service. */
export function buildServiceTopics(cfg: ServiceCaseConfig): CaseTopic[] {
  const topics: Array<{ slug: string; th: string; introTh: string; fixed: Parameters<typeof caseFor>[4] }> = [];

  for (const d of cfg.documents) {
    topics.push({
      slug: `doc-${d.slug}`,
      th: `เคส${cfg.th} — ${d.th}`,
      introTh: `รวมตัวอย่างเคสที่เอกสารหลักคือ${d.th} ในงาน${cfg.th} แต่ละเคสระบุว่าลูกค้าเป็นชาวไทยหรือชาวต่างชาติ อยู่จังหวัดหรือประเทศใด ใช้บริการใดร่วมด้วย เราดูแลขั้นตอนใด พบอุปสรรคอะไร และแก้ไขอย่างไร ข้อมูลใช้ประกอบการเตรียมเอกสารของท่านเอง`,
      fixed: {},
    });
  }
  for (const dest of DESTINATIONS) {
    topics.push({
      slug: `dest-${dest.slug}`,
      th: `เคส${cfg.th} — ปลายทาง${dest.th}`,
      introTh: `ตัวอย่างเคส${cfg.th}ที่ปลายทางอยู่ในกลุ่ม${dest.th} (${dest.countries.join(" · ")}) รูปแบบการรับรองและลำดับขั้นของแต่ละประเทศไม่เหมือนกัน เคสในหมวดนี้จึงช่วยให้เห็นว่าข้อกำหนดต่างกันตรงไหนก่อนเริ่มเตรียมเอกสาร`,
      fixed: { destinationTh: dest.countries[0]! },
    });
  }
  for (const region of REGION_SLUGS) {
    topics.push({
      slug: `area-${region}`,
      th: `เคส${cfg.th} — ลูกค้าใน${REGION_TH[region]}`,
      introTh: `ตัวอย่างเคส${cfg.th}ของลูกค้าที่อยู่ใน${REGION_TH[region]} ส่วนใหญ่ดำเนินการโดยไม่ต้องเดินทางเข้ามาเอง ใช้การรับ-ส่งเอกสารและการยืนยันร่างทางออนไลน์ เคสในหมวดนี้แสดงลำดับงานและช่วงเวลาโดยประมาณของการทำงานระยะไกล`,
      fixed: {},
    });
  }
  for (const a of AUDIENCES) {
    topics.push({
      slug: `who-${a.slug}`,
      th: `เคส${cfg.th} — กลุ่ม${a.th}`,
      introTh: `ตัวอย่างเคส${cfg.th}ของกลุ่ม${a.th} ซึ่งมีชุดเอกสารและกำหนดเวลาต่างจากกลุ่มอื่น เคสในหมวดนี้ช่วยให้เห็นเอกสารที่มักถูกขอเพิ่มและจุดที่ควรเผื่อเวลา`,
      fixed: { audienceTh: a.th },
    });
  }

  const perTopic = Math.ceil(TARGET_PER_SERVICE / topics.length);

  return topics.map((t, ti) => ({
    slug: t.slug,
    th: t.th,
    introTh: t.introTh,
    cases: Array.from({ length: perTopic }, (_, i) => {
      const seed = ti * 101 + i;
      const fixed = { ...t.fixed };
      if (t.slug.startsWith("area-")) {
        const region = t.slug.replace("area-", "");
        fixed.locationTh = pick(provincesByRegion(region), seed * 3 + 1);
      }
      return caseFor(cfg, t.slug, t.th, seed, fixed);
    }),
  }));
}

export function buildServiceCases(cfg: ServiceCaseConfig): CaseStudy[] {
  return buildServiceTopics(cfg).flatMap((t) => t.cases);
}
